import React, { useCallback } from "react";

import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faPenAlt } from "@fortawesome/pro-solid-svg-icons/faPenAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import List from "antd/es/list";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import ImportConfig from "@mapmycustomers/shared/types/imports/ImportConfig";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { AvatarWithName, Popconfirm } from "@mapmycustomers/ui";

import layout from "@app/styles/layout";
import { formatDate } from "@app/util/formatters";

import RenameConfigButton from "../RenameConfigButton";

import styles from "./ConfigListItem.module.scss";

interface Props {
  config: ImportConfig;
  onDelete: (id: ImportConfig["id"]) => void;
  onSelect: (config: ImportConfig) => void;
}

const ConfigListItem: React.FC<Props> = ({ config, onDelete, onSelect }) => {
  const intl = useIntl();

  const handleConfigClick = useCallback(() => onSelect(config), [config, onSelect]);
  const handleDeleteClick = useCallback(() => onDelete(config.id), [config, onDelete]);
  const [hovered, setHovered, resetHovered] = useBoolean();

  return (
    <List.Item
      className={styles.container}
      onClick={handleConfigClick}
      onMouseEnter={setHovered}
      onMouseLeave={resetHovered}
    >
      <List.Item.Meta
        className={styles.content}
        description={
          <div>
            <Row align="middle" justify="space-between">
              <Col>
                {intl.formatMessage({
                  id: "settings.newImports.config.configListPanel.lastModified.title",
                  defaultMessage: "Last Modified",
                  description: "Imports config list pane list order last modified date",
                })}
              </Col>
              <Col>{formatDate(config.updatedAt, "PPPp")}</Col>
            </Row>
            {config.user && (
              <Row align="middle" justify="space-between">
                <Col>
                  {intl.formatMessage({
                    id: "settings.newImports.config.configListPanel.creator.title",
                    defaultMessage: "Creator",
                    description: "Imports config list pane list order last modified date",
                  })}
                </Col>
                <Col>
                  <AvatarWithName avatarClassName={styles.creatorAvatar} user={config.user} />
                </Col>
              </Row>
            )}
          </div>
        }
        title={
          <Row justify="space-between">
            <Col className={styles.title}>{config.name}</Col>
            {hovered && (
              <Col>
                <Row className={styles.extraActions} gutter={layout.spacerM} onClick={stopEvents}>
                  <Col>
                    <RenameConfigButton config={config} showHelperText={false}>
                      <FontAwesomeIcon className={styles.renameIcon} icon={faPenAlt} />
                    </RenameConfigButton>
                  </Col>
                  <Col>
                    <Popconfirm
                      cancelText={intl.formatMessage({
                        id: "settings.newImports.config.configListPanel.deleteConfirmation.no",
                        defaultMessage: "Cancel",
                        description: "No-confirmation text for a Delete import config",
                      })}
                      description={intl.formatMessage({
                        id: "settings.newImports.config.configListPanel.deleteConfirmation",
                        defaultMessage: "Are you sure to delete this saved import matching?",
                        description: "Delete import  config confirmation",
                      })}
                      okText={intl.formatMessage({
                        id: "settings.newImports.config.configListPanel.deleteConfirmation.yes",
                        defaultMessage: "Delete",
                        description: "Yes-confirmation text for a Delete import config",
                      })}
                      onConfirm={handleDeleteClick}
                      placement="left"
                    >
                      <FontAwesomeIcon className={styles.deleteIcon} icon={faMinusCircle} />
                    </Popconfirm>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        }
      />
    </List.Item>
  );
};

export default ConfigListItem;
